import type { Unit } from './schema';

/**
 * Conversions d'unités (US-34) : « je veux cuisiner une recette américaine
 * sans convertir à la main ».
 *
 * Troisième endroit où un bug est **silencieux** — après le parsing et
 * l'adaptation des quantités — donc test unitaire obligatoire au sens du §1 de
 * CLAUDE.md. Une conversion fausse ne se voit pas : « 240 g de farine » a
 * exactement l'air d'une vraie mesure.
 *
 * Deux règles gouvernent ce fichier :
 *
 * 1. **La conversion est un affichage, jamais un enregistrement.** Le §3.2
 *    pose que les unités anglo-saxonnes sont canoniques et que le parseur ne
 *    les convertit pas : « 1 cup » reste `quantity=1, unit='cup'` en base.
 *    Ce module traduit à la lecture, et rien d'autre.
 * 2. **Une équivalence approximative est annoncée comme telle.** Une cuillère
 *    à soupe de farine ne pèse pas la même chose qu'une cuillère à soupe de
 *    miel ; donner « 10 g » sans le dire serait faux et crédible, ce que le §3
 *    cherche précisément à éviter.
 */

export type UnitSystem = 'metrique' | 'imperial';

/**
 * Équivalences **exactes**, celles du §3.2 : ce sont des définitions, pas des
 * approximations. Exprimées vers l'unité de base de leur catégorie (gramme,
 * millilitre).
 */
const EXACT_EQUIVALENTS: Partial<Record<Unit, { base: 'g' | 'ml'; value: number }>> = {
  g: { base: 'g', value: 1 },
  kg: { base: 'g', value: 1000 },
  oz: { base: 'g', value: 28.35 },
  lb: { base: 'g', value: 453.6 },

  ml: { base: 'ml', value: 1 },
  cl: { base: 'ml', value: 10 },
  l: { base: 'ml', value: 1000 },
  'c. à c.': { base: 'ml', value: 5 },
  'c. à s.': { base: 'ml', value: 15 },
  cup: { base: 'ml', value: 240 },
  tsp: { base: 'ml', value: 5 },
  tbsp: { base: 'ml', value: 15 },
};

/** L'unité vers laquelle chaque système traduit, par catégorie de base. */
const TARGET_UNIT: Record<UnitSystem, Record<'g' | 'ml', Unit>> = {
  metrique: { g: 'g', ml: 'ml' },
  imperial: { g: 'oz', ml: 'cup' },
};

/**
 * Système d'appartenance de chaque unité convertible. Sert à ne **pas**
 * convertir à l'intérieur d'un même système : « 2 c. à s. » est déjà une
 * mesure métrique parfaitement lisible, la traduire en « 30 ml » ferait perdre
 * la cuillère sans rien apporter. On ne traduit qu'en franchissant la
 * frontière.
 */
const UNIT_SYSTEM: Partial<Record<Unit, UnitSystem>> = {
  g: 'metrique',
  kg: 'metrique',
  ml: 'metrique',
  cl: 'metrique',
  l: 'metrique',
  'c. à c.': 'metrique',
  'c. à s.': 'metrique',

  oz: 'imperial',
  lb: 'imperial',
  cup: 'imperial',
  tsp: 'imperial',
  tbsp: 'imperial',
};

/** L'unité appartient-elle déjà au système demandé ? */
export function isInSystem(unit: Unit, system: UnitSystem): boolean {
  return UNIT_SYSTEM[unit] === system;
}

/**
 * Convertit une quantité vers le système demandé, ou `null` si l'unité n'a
 * pas d'équivalence exacte — c'est le cas de tout ce qui se compte (pièce,
 * gousse) et de toutes les mesures de maison (pot, verre). On ne convertit
 * pas ce qui ne se convertit pas.
 */
export function convertToSystem(
  quantity: number,
  unit: Unit,
  system: UnitSystem,
): { quantity: number; unit: Unit } | null {
  const equivalent = EXACT_EQUIVALENTS[unit];
  if (equivalent === undefined) return null;

  // Déjà dans le bon système : on ne touche à rien. Convertir « 2 c. à s. »
  // en « 30 ml » parce qu'on est en métrique ferait perdre la cuillère.
  if (isInSystem(unit, system)) return { quantity, unit };

  const target = TARGET_UNIT[system][equivalent.base];
  const targetEquivalent = EXACT_EQUIVALENTS[target];
  if (targetEquivalent === undefined) return null;
  if (target === unit) return { quantity, unit };

  return { quantity: (quantity * equivalent.value) / targetEquivalent.value, unit: target };
}

/**
 * Équivalences **approximatives** des mesures de maison, en grammes par unité
 * et par ingrédient. Une cuillère à soupe de farine (≈ 10 g) et une cuillère à
 * soupe de miel (≈ 21 g) ne pèsent pas la même chose : la table est donc
 * indexée par ingrédient, et tout ce qui n'y figure pas ne se convertit pas.
 *
 * Ces valeurs sont des ordres de grandeur de cuisine domestique, pas des
 * constantes physiques — d'où `approximate: true` sur tout ce qu'elles
 * produisent.
 */
const DOMESTIC_GRAMS: ReadonlyArray<{ unit: Unit; ingredient: string; grams: number }> = [
  { unit: 'c. à s.', ingredient: 'farine', grams: 10 },
  { unit: 'c. à s.', ingredient: 'sucre', grams: 15 },
  { unit: 'c. à s.', ingredient: 'huile', grams: 14 },
  { unit: 'c. à s.', ingredient: 'miel', grams: 21 },
  { unit: 'c. à s.', ingredient: 'beurre', grams: 15 },
  { unit: 'c. à c.', ingredient: 'farine', grams: 3 },
  { unit: 'c. à c.', ingredient: 'sucre', grams: 5 },
  { unit: 'c. à c.', ingredient: 'sel', grams: 6 },
  { unit: 'c. à c.', ingredient: 'levure', grams: 3 },
  { unit: 'verre', ingredient: 'eau', grams: 200 },
  { unit: 'verre', ingredient: 'farine', grams: 120 },
  { unit: 'verre', ingredient: 'sucre', grams: 160 },
  { unit: 'pot', ingredient: 'yaourt', grams: 125 },
  { unit: 'noix', ingredient: 'beurre', grams: 15 },
  { unit: 'pincee', ingredient: 'sel', grams: 1 },
];

const COMBINING_DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

/** Même repli que la recherche : accents, ligatures et casse. Sans « œ » → « oe », « œufs » ne trouverait pas « oeuf ». */
function fold(text: string): string {
  return text
    .replace(/œ/gi, 'oe')
    .replace(/æ/gi, 'ae')
    .normalize('NFD')
    .replace(COMBINING_DIACRITICS, '')
    .toLowerCase();
}

export interface WeightEstimate {
  grams: number;
  /** Toujours `true` ici : ces équivalences sont des ordres de grandeur, et le dire fait partie du résultat. */
  approximate: true;
}

/**
 * Poids approximatif d'une mesure de maison pour un ingrédient donné, ou
 * `null` si on ne sait pas — ce qui est le cas le plus fréquent, et de loin.
 * Ne jamais deviner (§3.2) : une valeur inventée serait crédible, donc pire
 * que pas de valeur.
 */
export function estimateGrams(quantity: number, unit: Unit, ingredientName: string): WeightEstimate | null {
  const folded = fold(ingredientName);
  const match = DOMESTIC_GRAMS.find((entry) => entry.unit === unit && folded.includes(fold(entry.ingredient)));
  if (match === undefined) return null;
  return { grams: quantity * match.grams, approximate: true };
}

/**
 * Substitutions courantes. Volontairement peu nombreuses et toutes vérifiables
 * en cuisine domestique : une liste exhaustive serait fausse quelque part, et
 * la valeur de cette fonction tient à ce qu'on puisse lui faire confiance.
 *
 * Chaque entrée dit **quoi** remplacer, **par quoi**, et à quelle condition —
 * parce qu'une substitution sans condition (« la crème par du yaourt ») est un
 * bon moyen de rater un plat.
 */
export interface Substitution {
  replacement: string;
  /** Ce qu'il faut savoir avant de le faire. Jamais vide : une substitution sans réserve n'existe pas. */
  caveat: string;
}

const SUBSTITUTIONS: ReadonlyArray<{ ingredient: string; options: readonly Substitution[] }> = [
  {
    ingredient: 'beurre',
    options: [
      { replacement: 'Huile (¾ du poids)', caveat: 'Convient aux pâtes et aux cakes, pas aux pâtes feuilletées.' },
      { replacement: 'Purée de pomme (moitié du poids)', caveat: 'Pour un gâteau seulement, et le résultat sera plus dense.' },
    ],
  },
  {
    ingredient: 'creme',
    options: [
      { replacement: 'Lait + beurre (¾ de lait, ¼ de beurre fondu)', caveat: 'Ne monte pas en chantilly.' },
      { replacement: 'Yaourt grec', caveat: 'À ajouter hors du feu : il tranche à l’ébullition.' },
    ],
  },
  {
    ingredient: 'oeuf',
    options: [
      { replacement: '½ banane écrasée', caveat: 'Pour lier un gâteau, et le goût se sent.' },
      { replacement: '1 c. à s. de graines de lin + 3 c. à s. d’eau', caveat: 'Lie, mais ne fait pas lever.' },
    ],
  },
  {
    ingredient: 'levure chimique',
    options: [
      { replacement: 'Bicarbonate + jus de citron (⅓ + 1 c. à c.)', caveat: 'À enfourner tout de suite : la réaction ne se garde pas.' },
    ],
  },
  {
    ingredient: 'lait',
    options: [
      { replacement: 'Boisson végétale non sucrée', caveat: 'Éviter les versions sucrées ou aromatisées en salé.' },
    ],
  },
  {
    ingredient: 'echalote',
    options: [{ replacement: 'Oignon (moitié moins)', caveat: 'Plus fort et moins fin : réduire la quantité.' }],
  },
  {
    ingredient: 'vin blanc',
    options: [{ replacement: 'Bouillon + une cuillère de vinaigre', caveat: 'L’acidité compte autant que le liquide.' }],
  },
];

/** Substitutions connues pour un ingrédient, ou tableau vide — ce qui est le cas normal. */
export function substitutionsFor(ingredientName: string): readonly Substitution[] {
  const folded = fold(ingredientName);
  return SUBSTITUTIONS.find((entry) => folded.includes(fold(entry.ingredient)))?.options ?? [];
}
